import { LoadDetailPlatform } from '@domain/platforms/load-detail-platform/domain/LoadDetailPlatform.model';
import {
  ActivityPlatformApiModel,
  ActivityPlatformHistoryApiModel,
  ActivityPlatformLiteApiModel,
  LoadDetailApiModel,
} from '../infrastructure/activity-platform-api.model';
import { DownloadDetailApiModel } from '@domain/platforms/download-detail-platform/infrastructure/download-detail-api.model';
import { DownloadDetail } from '@domain/platforms/download-detail-platform/domain/download-detail.model';
import {
  AutomobilePlatformApiModel,
  AutomobilePlatformLiteApiModel,
} from '@domain/platforms/automobile-platform/infrastructure/models/automobile-platform-api.model';
import {
  AutomobilePlatform,
  AutomobilePlatformLite,
} from '@domain/platforms/automobile-platform/domain/AutomobilePlatform.model';
import {
  ActivityPlatform,
  ActivityPlatformHistory,
  ActivityPlatformLite,
} from '../domain/activity-platform.model';
import { ActivityPlatformLogDataApiModel } from '../infrastructure/activity-platform-log-data-api.model';
import { ActivityPlatformLogData } from '../domain/activity-platform-log-data.model';
import { SetMapPictureAutomobilePlatforms } from '@domain/platforms/picture-automobile-platform/mappers/picture-automobile-platform.mappers';
import { mapIncidencePlatformApiModelsToIncidencePlatforms } from '@domain/platforms/incidence-platform/mappers/incidence-platform.mappers';

export const mapActivityPlatform = (
  data: ActivityPlatformApiModel
): ActivityPlatform => {
  return {
    id: data.ID,
    createdAt: data.CreatedAt,
    updatedAt: data.UpdatedAt,
    deletedAt: data.DeletedAt ?? null,
    uuid: data.uuid,
    typeActivityPlatformId: data.typeActivityPlatformId,
    fol: data.fol,
    createdBy: data.createdBy,
    updatedBy: data.updatedBy,
    isPaused: data.isPaused,
    isFinished: data.isFinished,
    finishedAt: data.finishedAt,
    finishedBy: data.finishedBy,
    shippingNoticeId: data.shippingNoticeId,
    loadDetail: data.loadDetail ? mapLoadDetail(data.loadDetail) : null,
    downloadDetail: data.downloadDetail
      ? mapDownloadDetail(data.downloadDetail)
      : null,
    automobilePlatformActivities: data.automobilePlatformActivities
      ? mapAutomobilePlatformActivities(data.automobilePlatformActivities)
      : null,
  };
};

export const mapActivityPlatformHistory = (
  data: ActivityPlatformHistoryApiModel
): ActivityPlatformHistory => {
  return {
    id: data.ID,
    createdAt: data.CreatedAt,
    updatedAt: data.UpdatedAt,
    uuid: data.uuid,
    typeActivityPlatformId: data.typeActivityPlatformId,
    fol: data.fol,
    createdBy: data.createdBy,
    updatedBy: data.updatedBy,
    isPaused: data.isPaused,
    isFinished: data.isFinished,
    finishedAt: data.finishedAt,
    finishedBy: data.finishedBy,
    shippingNoticeId: data.shippingNoticeId,
    typeActivity: data.typeActivity,
    loadDetail: data.loadDetail ? mapLoadDetail(data.loadDetail) : null,
    downloadDetail: data.downloadDetail
      ? mapDownloadDetail(data.downloadDetail)
      : null,
    automobilePlatformActivities: data.automobilePlatformActivities
      ? mapAutomobilePlatformActivitiesHistory(
          data.automobilePlatformActivities
        )
      : [],
  };
};

export const mapActivityPlatformLite = (
  data: ActivityPlatformLiteApiModel
): ActivityPlatformLite => {
  return {
    id: data.ID,
    createdAt: data.CreatedAt,
    updatedAt: data.UpdatedAt,
    deletedAt: data.DeletedAt ?? null,
    uuid: data.uuid,
    typeActivityPlatformId: data.typeActivityPlatformId,
    fol: data.fol,
    createdBy: data.createdBy,
    updatedBy: data.updatedBy,
    isPaused: data.isPaused,
    isFinished: data.isFinished,
    finishedAt: data.finishedAt,
    finishedBy: data.finishedBy,
    shippingNoticeId: data.shippingNoticeId,
    typeActivity: data.typeActivity,
  };
};

export const mapActivityPlatformLogDetail = (
  data: ActivityPlatformLogDataApiModel
): ActivityPlatformLogData => {
  return {
    id: data.id,
    operatorFullName: data.operatorFullName,
    container: data.container,
    truck: data.truck,
  };
};

export const mapAllActivityPlatforms = (
  data: ActivityPlatformApiModel[]
): ActivityPlatform[] => {
  return data.map(mapActivityPlatform);
};

export const mapAllActivitiesPlatformHistory = (
  data: ActivityPlatformHistoryApiModel[]
): ActivityPlatformHistory[] => {
  return data.map(mapActivityPlatformHistory);
};

const mapLoadDetail = (data: LoadDetailApiModel): LoadDetailPlatform => {
  return {
    id: data.ID,
    createdAt: data.CreatedAt,
    updatedAt: data.UpdatedAt,
    deletedAt: data.DeletedAt ?? null,
    ...data,
  };
};

const mapDownloadDetail = (data: DownloadDetailApiModel): DownloadDetail => {
  return {
    id: data.ID,
    createdAt: data.CreatedAt,
    updatedAt: data.UpdatedAt,
    deletedAt: data.DeletedAt ?? null,
    ...data,
  };
};

const mapAutomobilePlatformActivities = (
  data: AutomobilePlatformLiteApiModel[]
): AutomobilePlatformLite[] => {
  return data.map((activity) => {
    return {
      id: activity.ID,
      createdAt: activity.CreatedAt,
      updatedAt: activity.UpdatedAt,
      deletedAt: activity.DeletedAt ?? null,
      vin: activity.vin,
      isConfirmed: activity.isConfirmed,
      createdBy: activity.createdBy,
      updatedBy: activity.updatedBy,
    };
  });
};

const mapAutomobilePlatformActivityHistory = (
  data: AutomobilePlatformApiModel
): AutomobilePlatform => {
  return {
    id: data.ID,
    createdAt: data.CreatedAt,
    updatedAt: data.UpdatedAt,
    deletedAt: data.DeletedAt ?? null,
    vin: data.vin,
    isConfirmed: data.isConfirmed,
    createdBy: data.createdBy,
    updatedBy: data.updatedBy,
    activityPlatforms: null,
    picturesAutomobile: data.PicturesAutomobile
      ? SetMapPictureAutomobilePlatforms(data.PicturesAutomobile)
      : [],
    incidencesPlatform: data.IncidencesPlatform
      ? mapIncidencePlatformApiModelsToIncidencePlatforms(
          data.IncidencesPlatform
        )
      : [],
  };
};

export const mapAutomobilePlatformActivitiesHistory = (
  data: AutomobilePlatformApiModel[]
): AutomobilePlatform[] => {
  return data.map(mapAutomobilePlatformActivityHistory);
};

export const mapActivitiesPlatformLite = (
  data: ActivityPlatformLiteApiModel[]
): ActivityPlatformLite[] => {
  return data.map(mapActivityPlatformLite);
};
